import { inject, injectable } from "inversify";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import {
  BlinkProcessPaymentResponseModel,
  ClientDDFrequencies,
  ClientPortalPaymentModel,
  CreatePaymentResponseModel,
  DirectDebitSetupSuccessModel,
  FundScheduleChargeCategories,
  ICPLClientPortalPaymentsClient,
  MonthlyDirectDebitCalculationModel,
  QuarterlyDirectDebitCalculationModel,
  PaymentConfirmationModel,
  PaymentRequestModel,
} from "./api/cplapi/CPLAPI";

export interface IClientPortalPaymentsService {
  getClientPortalPayment(clientPortalPaymentID: string | undefined): Promise<ClientPortalPaymentModel>;
  createPaymentRequest(paymentRequestModel: PaymentRequestModel): Promise<CreatePaymentResponseModel>;
  processBlinkPayment(paymentDetailsJson: string): Promise<BlinkProcessPaymentResponseModel>;
  setPaymentConfirmationEmail(email: string, clientPortalPaymentID: string): Promise<void>;
  getAllowedDirectDebitCollectionDays(): Promise<Array<number>>;
  calculateMonthlyDirectDebitDetails(
    clientID: string,
    collectionDay: number,
    fundScheduleChargeCategoryID?: FundScheduleChargeCategories
  ): Promise<MonthlyDirectDebitCalculationModel>;
  calculateQuarterlyDirectDebitDetails(
    clientID: string,
    collectionDay: number,
    fundScheduleChargeCategoryID?: FundScheduleChargeCategories
  ): Promise<QuarterlyDirectDebitCalculationModel>;
  setUpDirectDebit(
    clientID: string,
    collectionDay: number,
    frequency: ClientDDFrequencies,
    fundScheduleChargeCategoryID?: FundScheduleChargeCategories
  ): Promise<DirectDebitSetupSuccessModel>;
  cancelDirectDebit(clientID: string, fundScheduleChargeCategoryID?: FundScheduleChargeCategories): Promise<void>;
}

@injectable()
export class ClientPortalPaymentsService implements IClientPortalPaymentsService {
  async setPaymentConfirmationEmail(email: string, clientPortalPaymentID: string): Promise<void> {
    return await this.cplClientPortalPaymentsClient.setPaymentConfirmationEmail(
      new PaymentConfirmationModel({ email, clientPortalPaymentID })
    );
  }
  @inject(SERVICE_TYPES.CPLCLientPortalPaymentsClient) cplClientPortalPaymentsClient!: ICPLClientPortalPaymentsClient;

  async getClientPortalPayment(clientPortalPaymentID: string | undefined): Promise<ClientPortalPaymentModel> {
    return await this.cplClientPortalPaymentsClient.getClientPortalPayment(clientPortalPaymentID);
  }

  async createPaymentRequest(paymentRequestModel: PaymentRequestModel): Promise<CreatePaymentResponseModel> {
    return await this.cplClientPortalPaymentsClient.createPaymentRequest(paymentRequestModel);
  }

  async processBlinkPayment(paymentDetailsJson: string): Promise<BlinkProcessPaymentResponseModel> {
    return await this.cplClientPortalPaymentsClient.processBlinkPayment(paymentDetailsJson);
  }

  async getAllowedDirectDebitCollectionDays(): Promise<Array<number>> {
    return await this.cplClientPortalPaymentsClient.getAllowedDirectDebitCollectionDays();
  }

  async calculateMonthlyDirectDebitDetails(
    clientID: string,
    collectionDay: number,
    fundScheduleChargeCategoryID?: FundScheduleChargeCategories
  ): Promise<MonthlyDirectDebitCalculationModel> {
    return await this.cplClientPortalPaymentsClient.calculateMonthlyDirectDebitDetails(
      clientID,
      collectionDay,
      fundScheduleChargeCategoryID
    );
  }

  async calculateQuarterlyDirectDebitDetails(
    clientID: string,
    collectionDay: number,
    fundScheduleChargeCategoryID?: FundScheduleChargeCategories
  ): Promise<QuarterlyDirectDebitCalculationModel> {
    return await this.cplClientPortalPaymentsClient.calculateQuarterlyDirectDebitDetails(
      clientID,
      collectionDay,
      fundScheduleChargeCategoryID
    );
  }

  async setUpDirectDebit(
    clientID: string,
    collectionDay: number,
    frequency: ClientDDFrequencies,
    fundScheduleChargeCategoryID?: FundScheduleChargeCategories
  ): Promise<DirectDebitSetupSuccessModel> {
    return await this.cplClientPortalPaymentsClient.setUpDirectDebit(
      clientID,
      collectionDay,
      frequency,
      fundScheduleChargeCategoryID
    );
  }

  async cancelDirectDebit(
    clientID: string,
    fundScheduleChargeCategoryID?: FundScheduleChargeCategories
  ): Promise<void> {
    return await this.cplClientPortalPaymentsClient.cancelDirectDebit(clientID, fundScheduleChargeCategoryID);
  }
}
